/**
 * Island Mounter
 *
 * Mounts interactive React components ("islands") into server-rendered
 * static HTML content. This enables interactivity while preserving
 * the SEO benefits of pre-rendered content.
 *
 * Islands are identified by `data-island` attributes in the static HTML.
 * Props are stored in `data-*` attributes as JSON or primitive values.
 */

import { createRoot, type Root } from "react-dom/client";
import {
  TabbedConversationSSR,
  type TabDefinition,
} from "@/client-integrations/Conversation";
import { Diagram } from "@/components/Diagram";
import { PreferencesStoreProvider } from "@/stores/preferences/preferences-provider";
import { getInitialThemeMode, getInitialThemePreset } from "@/lib/theme-utils";

// Island types we support
type IslandType = "tabbed-conversation" | "diagram" | "themed-image";

/**
 * Mount all islands within a container element.
 * Returns a cleanup function to unmount all mounted components.
 */
export function mountIslands(container: HTMLElement): () => void {
  const roots: Root[] = [];
  const islands = container.querySelectorAll<HTMLElement>("[data-island]");

  // Read theme once for all islands (avoids repeated localStorage access)
  const themeMode = getInitialThemeMode();
  const themePreset = getInitialThemePreset();

  islands.forEach((el) => {
    const type = el.getAttribute("data-island") as IslandType;

    // Skip ThemedImage - it's fully static (CSS handles light/dark switching)
    if (type === "themed-image") {
      return;
    }

    // Find or create mount point (sibling to placeholder, preserves SSR content during load)
    let mountPoint = el.querySelector<HTMLElement>("[data-island-mount]");
    if (!mountPoint) {
      mountPoint = document.createElement("div");
      mountPoint.setAttribute("data-island-mount", "true");
      mountPoint.style.display = "none"; // Hidden until ready
      el.appendChild(mountPoint);
    }

    const root = createRoot(mountPoint);
    roots.push(root);

    // Find SSR placeholder
    const placeholder = el.querySelector(
      ".ssr-tabs-placeholder, .ssr-diagram-placeholder",
    );

    try {
      switch (type) {
        case "tabbed-conversation": {
          const height = parseInt(el.getAttribute("data-height") || "500", 10);
          const tabsJson = el.getAttribute("data-tabs") || "[]";
          let tabs: TabDefinition[];

          try {
            tabs = JSON.parse(tabsJson);
          } catch {
            console.error("[Islands] Invalid tabs JSON:", tabsJson);
            return;
          }

          // Render with PreferencesStoreProvider so TabbedConversationSSR
          // can access the preferences store (themeMode) via usePreferencesStore
          root.render(
            <PreferencesStoreProvider
              themeMode={themeMode}
              themePreset={themePreset}
            >
              <TabbedConversationSSR
                tabs={tabs}
                height={height}
                onReady={() => {
                  // Show mount point and remove placeholder AFTER component is ready
                  if (mountPoint) {
                    mountPoint.style.display = "";
                  }
                  placeholder?.remove();
                }}
              />
            </PreferencesStoreProvider>,
          );
          break;
        }

        case "diagram": {
          const propsJson = el.getAttribute("data-props") || "{}";
          let props: {
            src: string;
            alt: string;
            caption?: string;
            mermaid?: string;
          };

          try {
            props = JSON.parse(propsJson);
          } catch {
            console.error("[Islands] Invalid diagram props JSON:", propsJson);
            return;
          }

          // Render the interactive diagram with lightbox/download
          // Also wrap with PreferencesStoreProvider for theme-aware rendering
          root.render(
            <PreferencesStoreProvider
              themeMode={themeMode}
              themePreset={themePreset}
            >
              <DiagramIsland
                {...props}
                onReady={() => {
                  if (mountPoint) {
                    mountPoint.style.display = "";
                  }
                  placeholder?.remove();
                }}
              />
            </PreferencesStoreProvider>,
          );
          break;
        }

        default:
          console.warn(`[Islands] Unknown island type: ${type}`);
      }
    } catch (error) {
      console.error(`[Islands] Failed to mount "${type}":`, error);
      // On error, leave placeholder in place
    }
  });

  // Return cleanup function
  return () => {
    roots.forEach((root) => {
      try {
        root.unmount();
      } catch {
        // Ignore unmount errors
      }
    });
  };
}

/**
 * Diagram island wrapper that signals ready after mount.
 */
function DiagramIsland({
  src,
  alt,
  caption,
  mermaid,
  onReady,
}: {
  src: string;
  alt: string;
  caption?: string;
  mermaid?: string;
  onReady?: () => void;
}) {
  // Use effect to signal ready after first render
  // Using double RAF to ensure paint has completed
  if (typeof window !== "undefined" && onReady) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onReady();
      });
    });
  }

  return <Diagram src={src} alt={alt} caption={caption} mermaid={mermaid} />;
}
